/**
 * One player row's identity.
 *
 * The row subscribes to identity ONLY. Everything that changes during play —
 * the clock, the captured tray and the first-move countdown — is its own
 * memoised leaf with its own subscription, so an opponent move costs 0
 * PlayerRow renders (UX spec §6a).
 *
 * `firstMoveUp` arrived with decision D2: the first-move countdown is a BANNER
 * on the owing player's row (the app's shape, ported here), not the anonymous
 * dock chip the M1b lane had. The old row shows the banner INSTEAD of the
 * captured tray (`{!showFirstMoveTimer && <CapturedPieces …>}`,
 * `PlayerComponent` L117) and the row is what owns that slot. It is a plain
 * store read — whether the window is open is not time-dependent — so it costs
 * one row render when the banner appears and one when it goes, and ZERO per
 * opponent move.
 *
 * `hasSeat` (fix G / G5, 2026-10-06): the Stage mounts the Stage/PlayerRow
 * pair for the idle tournament view now too (`rail/Rail/model.ts
 * selectRailMode`), where `game.whiteId`/`blackId` are both null — without
 * this, `selectSeat`'s own fallback reads as "Opponent" on BOTH rows. A plain
 * store read, same render cost as `isMe`: it flips at most twice per GAME
 * (a pairing lands, the game ends and is reset), never per move.
 */
import {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {
  selectBoardSource,
  selectSeat,
  selectWatchSeat,
} from '@/Store/liveGame/selectors';
import type {Side} from '@/Store/liveGame/state';

import {selectFirstMoveFor} from './FirstMoveBanner.model';

export type PlayerRowModel = {
  name: string;
  rating: number | null;
  colorLabel: string;
  isMe: boolean;
  /** the first-move banner owns the tray slot while it is up */
  firstMoveUp: boolean;
  /** false on the idle tournament view (G5): no name, no clock */
  hasSeat: boolean;
};

export function usePlayerRowModel(side: Side): PlayerRowModel {
  // M4: the row reads the WATCHED seat while a game is being spectated or
  // replayed. `selectBoardSource` is a string, so a write to the watch slice
  // outside a watch cannot re-render this row.
  const source = useLiveGame(selectBoardSource);
  const selector = useMemo(
    () => (source === 'watch' ? selectWatchSeat(side) : selectSeat(side)),
    [source, side],
  );
  const seat = useLiveGame(useShallow(selector));
  const firstMove = useMemo(() => selectFirstMoveFor(side), [side]);
  // there is no first move to owe in a game that is not mine
  const firstMoveUp = useLiveGame(
    s => source !== 'watch' && firstMove(s).armed,
  );
  const hasSeat = useLiveGame(
    s =>
      source === 'watch' ||
      !!(side === 'white' ? s.game.whiteId : s.game.blackId),
  );

  return {
    name: hasSeat ? seat.name : '—',
    rating: seat.rating,
    colorLabel: seat.colorLabel,
    isMe: seat.isMe,
    firstMoveUp,
    hasSeat,
  };
}
